/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { Popconfirm, message as messageAnt } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from 'react-query';
import QuickViewCard from '../../components/QuickViewCard';
import { PartyIcon } from '../../../../../Layout/Report/components/Icons';
import { deleteRequest, getRequest } from '../../../../../../core/apiRequests';

const AutoMessageCard = ({
  id,
  message,
  scheduled_date_time,
  FullName,
  repeat_scale,
  repeat_interval,
  repeats,
  client_name,
  associate_name,
  relation,
  review_link,
  client,
  auth,
  onDelete = () => { },
}) => {
  const [expanded, setExpanded] = useState(false);

  // Fetch associates data to resolve assigned associate name
  const { data: associates } = useQuery(
    'associates-auto-message-card',
    async () => {
      const reqObj = {
        params: ['chats', 'getAsctList'],
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      return data?.map(asct => ({
        id: asct?.id,
        name: `${asct?.first_name || ''} ${asct?.last_name || ''}`.trim(),
      })) || [];
    },
    {
      enabled: !!auth?.merchantId,
      refetchOnWindowFocus: false,
      initialData: [],
    }
  );

  // Fetch the merchant's stores to resolve the client's preferred store name from store_id
  const { data: stores } = useQuery(
    'stores-auto-message-card',
    async () => {
      const reqObj = {
        params: ['stores'],
        loadingStatusOff: true,
      };
      const data = await getRequest(reqObj);
      return data?.map(st => ({ id: st?.id, name: st?.name })) || [];
    },
    {
      enabled: !!auth?.merchantId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  // Get the client's preferred store name, falling back to the name on the client record
  const getStoreName = useCallback(() => {
    const store = stores.find(st => Number(st.id) === Number(client?.store_id));
    return store?.name || client?.store_name || null;
  }, [stores, client?.store_id, client?.store_name]);

  // Get assigned associate name
  const getAssignedAssociateName = useCallback(() => {
    if (!client?.associate_id) return null;
    const assignedAssociate = associates.find(asct => asct.id === client.associate_id);
    return assignedAssociate?.name || null;
  }, [associates, client?.associate_id]);

  const getLines = useMemo(() => {
    const el = document.getElementById(`item-expanded-span-${id}`);
    if (el) {
      return el.scrollHeight > el.clientHeight || (el.scrollHeight === 0 && el.clientHeight === 0);
    }
    return false;
  }, [id, message]);

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const messageFormat = useCallback(() => {
    if (message) {
      let formatted = message
        .replaceAll(':name:', client_name)
        .replaceAll(':associate:', associate_name)
        .replaceAll(':relation_name:', relation)
        .replaceAll(':review:', review_link);

      // Handle :assigned_associate_name: template variable
      if (message.includes(':assigned_associate_name:')) {
        const assignedAssociateName = getAssignedAssociateName();
        formatted = formatted.replaceAll(':assigned_associate_name:', assignedAssociateName || ':assigned_associate_name:');
      }

      // Handle :store: template variable
      if (message.includes(':store:')) {
        formatted = formatted.replaceAll(':store:', getStoreName() || ':store:');
      }

      return formatted;
    }
    return '';
  }, [message, client_name, associate_name, review_link, relation, getAssignedAssociateName, getStoreName]);

  const handleDelete = useCallback(async () => {
    try {
      const reqObj = {
        params: ['chatMessages', 'autoMessages', 'deleteClientAutoMsg'],
        query: { id },
        loadingStatusOff: true,
      };
      await deleteRequest(reqObj);
      messageAnt.success('Message Successfully Deleted!');
      onDelete(id);
    } catch (error) {
      console.error('Error deleting auto message:', error);
      messageAnt.error('Failed To Delete Message');
    }
  }, [id]);

  return (
    <QuickViewCard
      borderColor={null}
      title={messageFormat()}
      action={null}
      containerStyle={{ position: 'relative', width: '100%' }}
      titleClassName={expanded ? '' : 'line-clamp-1'}
      titleId={`item-expanded-span-${id}`}
      hasFooter
      hasEdit={false}
      dividerStyles={{ margin: '0px 0 8px' }}
      actionButton={
        <Popconfirm
          title="Delete the auto message"
          description="Are you sure to delete this auto message?"
          onConfirm={handleDelete}
          okText="Yes"
          cancelText="No"
        >
          <div style={{ color: '#d93b3b' }} className="fw-500 pointer fs-14">
            Delete
          </div>
        </Popconfirm>
      }
    >
      <span className="fs-13 m-top-4" style={{ color: '#0a0a0a66' }}>{`Due on ${moment(
        scheduled_date_time,
        'YYYYMMDDhhmm',
      ).format('MMM DD, YYYY')}`}</span>
      <span className="fs-13 m-top-4" style={{ color: '#0a0a0a66' }}>
        {FullName === 'Birthday' || FullName === 'Anniversary' ? (
          <PartyIcon style={{ marginRight: '18px' }} />
        ) : (
          <FontAwesomeIcon icon="fa-sharp fa-solid fa-dollar-sign" style={{ marginRight: '18px' }} />
        )}
        {FullName}
      </span>
      {!!Number(repeats) && (
        <span className="fs-13 m-top-4" style={{ color: '#0a0a0a66' }}>
          <FontAwesomeIcon icon={['fa', 'refresh']} style={{ marginRight: '18px' }} />
          {Number(repeats)
            ? `Repeats every ${repeat_scale} ${repeat_interval?.toLowerCase()}${parseInt(repeat_scale, 10) > 1 ? 's' : ''
            }`
            : ''}
        </span>
      )}
      {getLines && (
        <div
          style={{ color: '#9C9C9C', position: 'absolute', bottom: '4px', right: '4px', cursor: 'pointer' }}
          onClick={toggleExpanded}
        >
          {!expanded ? (
            <FontAwesomeIcon icon="fa-light fa-arrow-up-right-and-arrow-down-left-from-center" />
          ) : (
            <FontAwesomeIcon icon="fa-light fa-arrow-down-left-and-arrow-up-right-to-center" />
          )}
        </div>
      )}
    </QuickViewCard>
  );
};

export default AutoMessageCard;
